import React, { useEffect, useState, useMemo } from "react";
import {
  catchError,
  filter,
  map,
  of,
  tap,
  combineLatest,
  switchMap,
} from "rxjs";
import PropTypes from "prop-types";
import withErrorGuard from "../../HOC/withErrorGuard";
import getSampleTracks from "./getSampleBrowserTracks";
import getApiClient from "../Pulsar/apiClient";
import clamp from "../../common/helpers/clamp";
import WebglErrorView from "../../HOC/withErrorGuard/WebglErrorView";
import Pulsar from "../Pulsar";
import {
  CONSERVATION_CL,
  PATHOGENICITY_CL,
  PHASING_CL,
  ROH_CL,
  TRANSCRIPT_CL,
  IMPACT_NULL_VARS_CL,
  IMPACT_OTHER_CL,
} from "../Pulsar/constants/options";

function SampleViewInternal({
  genome,
  acmgTranscript,
  cnvTranscript,
  position,
  queryType,
  variantId,
  refLength,
  mainTranscriptLength,
  cnvLength,
  analysisId,
  hasPhasingGroups,
  rootAnalysis,
}) {
  const manyWarning = "Sample View can support up to 10 samples.";
  const [warning, setWarning] = useState("");
  const [error, setError] = useState(null);
  const [transcriptPos, setTranscriptPos] = useState([Infinity, -Infinity]);
  const [tracks, setTracks] = useState([]);
  const selectedTranscript = acmgTranscript || cnvTranscript;

  useEffect(() => {
    const { loadAnalysisInfo, loadAnalysisSamples } = getApiClient();
    const sub = combineLatest([
      loadAnalysisInfo(rootAnalysis).pipe(catchError(() => of(null))),
      loadAnalysisSamples(analysisId),
    ])
      .pipe(
        map((res) => ({ info: res[0], samples: res[1] })),
        tap((res) => setWarning(res.samples.length > 10 ? manyWarning : "")),
        map(({ info, samples }) => getSampleTracks({ info, samples })),
        tap((sampleTracks) => setTracks(sampleTracks)),
        tap(() => setError(null)),
        catchError(() => {
          setError("Failed to load analysis info.");
          return of(null);
        })
      )
      .subscribe(() => {});

    return () => sub.unsubscribe();
  }, [rootAnalysis, analysisId]);

  useEffect(() => {
    if (!selectedTranscript) {
      setTranscriptPos([Infinity, -Infinity]);
      return () => {};
    }

    const { loadTranscriptInfo: loadTranscript } = getApiClient();
    const genomeId = genome;
    const sub = of(null)
      .pipe(
        switchMap(() => loadTranscript({ genomeId, selectedTranscript })),
        filter((x) => x.coords && x.coords[0]),
        map((res) => res.coords[0]),
        map((res) => [res.position, res.position + res.length]),
        catchError(() => of([Infinity, -Infinity])),
        tap(setTranscriptPos)
      )
      .subscribe();

    return () => sub.unsubscribe();
  }, [genome, selectedTranscript]);

  const rangeLength = useMemo(() => {
    return queryType === "cnv" ? cnvLength : refLength;
  }, [queryType, mainTranscriptLength, cnvLength, refLength]);

  const [variantStart, variantEnd] = useMemo(() => {
    const middle = position + rangeLength * 0.5;
    const visRange = clamp(rangeLength, 50, 4000000000);
    const x0 = middle - visRange * 0.5;
    const x1 = middle + visRange * 0.5;
    return [x0, x1];
  }, [position, rangeLength]);

  const start = Math.min(variantStart, transcriptPos[0]);
  const end = Math.max(variantEnd, transcriptPos[1]);

  if (error) {
    return <div style={{ minHeight: "calc(100vh - 200px)" }}>{error}</div>;
  }

  if (!tracks || !tracks.length) {
    return (
      <div style={{ minHeight: "calc(100vh - 200px)" }}>
        <div className="tw-italic tw-flex tw-p-4 tw-justify-center tw-bg-blueGray-200 tw-border tw-border-blueGray-300">
          <div className="tw-inline-block tw-mx-4">Loading...</div>
          <div className="tw-inline-block">
            <i className="fa fa-circle-o-notch fa-spin" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <Pulsar
      title={
        <div>
          Sample View
          {warning ? (
            <i
              className="fa fa-exclamation-circle tw-mx-2 tw-text-red-500 tw-fill-current"
              data-tooltip={warning}
              data-tooltip-class="genome-browser-small-tooltip"
            />
          ) : null}
        </div>
      }
      key="sample-view"
      trackConfs={tracks}
      visStart={start}
      visEnd={end}
      start={start}
      end={end}
      allowedColorScales={[
        CONSERVATION_CL,
        PATHOGENICITY_CL,
        ROH_CL,
        TRANSCRIPT_CL,
        PHASING_CL,
      ]}
      allowedColorScaleCategories={[
        {
          name: "Coding Impact",
          scales: [IMPACT_NULL_VARS_CL, IMPACT_OTHER_CL],
        },
      ]}
      state={{
        analysisId,
        rootAnalysis,
        selectedTranscript,
        acmgTranscript,
        genome,
        selectedVariantId: variantId,
        hasPhasingGroups,
      }}
    />
  );
}

SampleViewInternal.propTypes = {
  rootAnalysis: PropTypes.string.isRequired,
  analysisId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  refLength: PropTypes.number,
  cnvLength: PropTypes.number,
  position: PropTypes.number.isRequired,
  variantId: PropTypes.string,
  mainTranscriptLength: PropTypes.number,
  queryType: PropTypes.string.isRequired,
  acmgTranscript: PropTypes.string,
  genome: PropTypes.string,
  cnvTranscript: PropTypes.string,
  hasPhasingGroups: PropTypes.bool,
};

SampleViewInternal.defaultProps = {
  variantId: null,
  genome: null,
  mainTranscriptLength: 0,
  refLength: 0,
  cnvLength: 0,
  acmgTranscript: null,
  cnvTranscript: "",
  hasPhasingGroups: false,
};

export default withErrorGuard(
  SampleViewInternal,
  "SampleView",
  "Sample View",
  WebglErrorView
);
